import Button from '@allesauto/core/button';
import Carousel from '@allesauto/core/carousel';
import Headline from '@allesauto/core/headline';
import NextLink from '@allesauto/core/nextLink';
import { ResponsiveImage } from '@allesauto/core/responsiveImage';
import {
  FieldTypeAsset,
  FieldTypeLink,
  HeadlineVariation,
} from '@allesauto/utils/consts';
import { getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { Maximize16 } from '@carbon/icons-react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React from 'react';
import { FC } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

enum Colors {
  PURPLE = 'PURPLE',
  GREEN = 'GREEN',
  GREY = 'GREY',
  WHITE = 'WHITE',
  RED = 'RED',
  BLACK = 'BLACK',
  DARK_GREY = 'DARK_GREY',
}

type CiColors = typeof Colors;

export interface GalleryImage {
  component: string;
  label: string;
  labelColor: keyof Omit<CiColors, Colors.BLACK>;
  labelBackground: Colors;
  link: FieldTypeLink;
  image: FieldTypeAsset;
}

export interface ImageGalleryProps {
  component: string;
  _uid: string;
  _editable?: string;
  headline: string;
  description: string;
  images: GalleryImage[];
  buttonLabel?: string;
  buttonLink?: FieldTypeLink;
  background: keyof Omit<CiColors, Colors.RED>;
  buttonColor: keyof Omit<CiColors, Colors.BLACK | Colors.GREY>;
  buttonTextColor: Colors.WHITE | Colors.BLACK;
  headlineColor: keyof Omit<
    CiColors,
    Colors.RED | Colors.BLACK | Colors.DARK_GREY
  >;
}

const cssClasses = {
  mainContainer: clsx('h-auto py-20px md:py-100px'),
  container: clsx('bg-purple80 monochrome:bg-grey90'),
  headerGrid: clsx('grid grid-cols-24'),
  headerTextArea: clsx('col-end-24 col-start-2'),
  headline: clsx(
    'mb-20px px-15px z-20 sm:mb-0 sm:pb-20px md:my-60px md:pl-0 md:pr-20px md:py-20px',
    'h-max md:col-end-10 md:col-start-1',
    'bg-purple80 monochrome:bg-grey90'
  ),
  description: clsx('font-regular font-weight-normal mt-[12px] text-p1'),
  buttonContainer: clsx('align-center flex justify-center md:mt-20px'),
};

const theme = {
  [Colors.PURPLE]: {
    container:
      'bg-purple80 border-purple80 monochrome:bg-purple80 hover:border-purple80',
    text: 'text-purple80 monochrome:text-purple80',
  },
  [Colors.GREEN]: {
    container:
      'bg-green60 border-green60 monochrome:bg-green60 hover:border-green60',
    text: 'text-green60 monochrome:text-green60',
  },
  [Colors.GREY]: {
    container: 'bg-grey20 border-grey20 monochrome:bg-grey20',
    text: 'text-grey80 monochrome:text-grey80',
  },
  [Colors.DARK_GREY]: {
    container:
      'bg-grey80 border-grey80 monochrome:bg-grey80 hover:border-grey80',
    text: 'text-grey80 monochrome:text-grey80',
  },
  [Colors.WHITE]: {
    container: 'bg-white border-white monochrome:bg-white hover:border-white',
    text: 'text-white monochrome:text-white',
  },
  [Colors.BLACK]: {
    text: 'text-black monochrome:text-black',
    container: 'bg-black border-black monochrome:bg-black hover:border-black',
  },
  [Colors.RED]: {
    container: 'bg-red60 border-red60 monochrome:bg-red60 hover:border-red60',
    text: 'text-red60 monochrome:text-red60',
  },
};

const ImageGallery: FC<React.PropsWithChildren<ImageGalleryProps>> = (
  props
) => {
  const [open, setOpen] = React.useState(false);
  const [currentImage, setCurrentImage] = React.useState(0);
  const {
    _uid,
    headline,
    description,
    images,
    buttonLabel,
    buttonLink = '',
    background = Colors.PURPLE,
    buttonColor = Colors.RED,
    buttonTextColor = Colors.WHITE,
    headlineColor = Colors.WHITE,
  } = props;
  const showCtaButton = buttonLabel && buttonLink;

  const slides = images.slice(0, images.length).map((i) => ({
    src: i.image.filename,
    title: i.label ?? '',
  }));

  return (
    <div
      {...sbEditable(props)}
      key={_uid}
      className={clsx(cssClasses.mainContainer, theme[background].container)}
    >
      <div className={cssClasses.headerGrid}>
        <div className={cssClasses.headerTextArea}>
          <Headline
            headlineTag={HeadlineVariation.H2}
            headlineColor={theme[headlineColor].text}
            headlineText={headline}
          />
          <p
            className={clsx(cssClasses.description, theme[headlineColor].text)}
          >
            {description}
          </p>
        </div>
      </div>
      <Carousel
        partialVisible
        partialVisibilityGutter={30}
        isInfinite={false}
        itemsXLDesktop={2}
        itemsToScrollXLDesktop={2}
        itemsLDesktop={2}
        darkBackground={background === Colors.PURPLE}
        itemsToScrollLDesktop={2}
        classNames={{
          sectionClass: 'bg-transparent !pb-0 sm:grid-cols-1 lg:grid-cols-24',
          containerClass:
            images.length === 0
              ? 'justify-center px-[10px] md:pb-40px lg:pb-40px'
              : '',
          dotListClass: clsx(
            'react-multi-carousel-dot-dark-bg',
            theme[background].container,
            ' !mb-0 gap-10px p-30px'
          ),
        }}
      >
        {images &&
          images.map((image, index) => {
            const isLastItem = index + 1 === images.length;
            const link = image.link ? getUrlFromStoryblokLink(image.link) : '';
            return (
              <div
                className={clsx(
                  'ml-[16px] relative lg:mx-0',
                  isLastItem && 'mr-[16px]'
                )}
                key={image.image.id}
              >
                <div
                  onClick={() => {
                    setCurrentImage(index);
                    setOpen(true);
                  }}
                  className="absolute bg-white cursor-pointer p-2 p-7px right-15px rounded-circle top-15px z-10"
                >
                  <Maximize16 />
                </div>
                <div>
                  {' '}
                  <ResponsiveImage
                    alt={image.image.alt}
                    title={image.image.title}
                    aspectRatio={16 / 10}
                    src={image.image.filename}
                    // if imageSrc is a string then it was not resized and we need to fit it into the defined aspect ratio
                    style={
                      typeof image.image.filename === 'string'
                        ? { objectFit: 'cover' }
                        : undefined
                    }
                    loading={'eager'}
                  />
                  {image.label && (
                    <div
                      onClick={
                        link ? () => window.open(link, '_blank') : undefined
                      }
                      className={clsx(
                        'absolute bottom-0 box-border flex font-weight-bold h-12 items-center justify-center p-10px text-headline4 w-full',
                        image.link ? 'cursor-pointer' : 'cursor-default',
                        theme[image.labelBackground || Colors.GREEN].container,
                        theme[image.labelColor || Colors.PURPLE].text
                      )}
                    >
                      {image.label}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </Carousel>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={currentImage}
        plugins={[Captions]}
        slides={slides}
        carousel={{
          finite: false,
          preload: 2,
          imageFit: 'contain',
          padding: '64px',
          spacing: '64px',
        }}
      />
      {showCtaButton && (
        <div className={cssClasses.buttonContainer}>
          <NextLink href={getUrlFromStoryblokLink(buttonLink)}>
            <Button
              text={buttonLabel}
              textColor={theme[buttonTextColor]?.text}
              customClassName={`!${theme[buttonColor].container} !${theme[buttonTextColor]?.text}`}
            />
          </NextLink>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
